import { useEffect } from 'react';

// File names of everything the app produces (the user's rule, 2026-10-20): a document is the customer's name run together
// in CamelCase plus what it is — "Clínica Prueba SRL" + factura → ClinicaPruebaSrlFactura.pdf — and a report is its plain
// name, "Reporte de Cobros.pdf". The browser names a printout saved as PDF after the page's title, so the print views set
// it (useFileName). Mirrored in backend/utils/fileName.js for the PDFs the server makes — keep them in step.

// Accents and punctuation dropped, each word capitalized; a word in all capitals is lowered after its first letter, so
// "CLÍNICA PRUEBA, S.R.L." → "ClinicaPruebaSRL" and "IntMed" stays "IntMed".
export const camelName = (name) => String(name || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .split(/[^A-Za-z0-9]+/)
  .filter(Boolean)
  .map(w => (w === w.toUpperCase() ? w.charAt(0) + w.slice(1).toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1)))
  .join('');

// What a document is, as it goes in the name.
export const DOC_TYPES = {
  factura: 'Factura',
  notaCredito: 'NotaDeCredito',
  notaDebito: 'NotaDeDebito',
  cotizacion: 'Cotizacion',
  conduce: 'Conduce',
  conduceRetiro: 'ConduceDeRetiro',
  contrato: 'ContratoDeAlquiler',
  reciboIngreso: 'ReciboDeIngreso',
  reciboTransporte: 'ReciboDeTransporte',
  carta: 'Carta',
};
export const invoiceDocType = (tipoEcf) => ({ E33: DOC_TYPES.notaDebito, E34: DOC_TYPES.notaCredito }[tipoEcf] || DOC_TYPES.factura);

// The name without extension (a page title; the browser adds .pdf).
export const documentName = (customerName, docType) => `${camelName(customerName)}${docType}`;

// "Cobros" → "Reporte de Cobros"; a title that already says Reporte stays as it is; "607 — Ventas" → "Reporte 607 — Ventas".
export const reportName = (title) => {
  const t = String(title || '').trim();
  if (!t) return 'Reporte';
  if (/^reporte\b/i.test(t)) return t;
  return /^\d/.test(t) ? `Reporte ${t}` : `Reporte de ${t}`;
};

// Sets the page's title while a print view is open (so "Guardar como PDF" offers that name) and puts it back after.
export function useFileName(name) {
  useEffect(() => {
    if (!name) return undefined;
    const previous = document.title;
    document.title = name;
    return () => { document.title = previous; };
  }, [name]);
}
